import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { MessageCircle, Send, Clock3, ShieldCheck, ChevronDown } from 'lucide-react';
import AuthorAvatar from '../common/AuthorAvatar';
import { commentApi } from '../../utils/api';
import { formatPublishNp } from '../../utils/timeAgo';

const MAX_CHARS = 50;
const PAGE_SIZE = 6;

// ============================================================
// MODERN comment section —
//  • gradient header badge + count pill
//  • form ma live avatar preview + character progress bar
//  • chat-bubble style comments + Nepali relative time ("५ मिनेट अघि")
//  • admin reply = official gradient-bar quote
//  • empty state CTA + "थप हेर्नुहोस्" load-more (scroll box hataiyeko)
// ============================================================
function CommentSection({ articleId }) {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchComments();
    setVisibleCount(PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await commentApi.getByNews(articleId);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const response = await commentApi.create({
        news_id: articleId,
        name: name.trim() || 'अज्ञात पाठक',
        comment: text.trim(),
      });
      setComments([response.data, ...comments]);
      setText('');
      toast.success('प्रतिक्रिया पठाइयो।');
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error(error.response?.data?.error || 'प्रतिक्रिया पठाउन असफल भयो।');
    } finally {
      setSending(false);
    }
  };

  const pct = Math.min(100, (text.length / MAX_CHARS) * 100);
  const nearLimit = text.length >= MAX_CHARS * 0.8;

  return (
    <section className="mt-11 pt-7 border-t-2 border-line">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-5">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-crimson to-crimson-dark text-white flex items-center justify-center shadow-lg shadow-crimson/25">
          <MessageCircle size={16} />
        </span>
        <h3 className="font-display text-[19px] font-bold m-0">प्रतिक्रिया</h3>
        <span className="min-w-[26px] text-center px-2 py-0.5 rounded-full bg-crimson/10 text-crimson text-[12px] font-extrabold leading-relaxed">
          {loading ? '…' : comments.length}
        </span>
      </div>

      {/* Composer */}
      <div className="bg-white border border-line rounded-2xl p-3.5 sm:p-4 shadow-sm mb-6">
        <div className="flex gap-3">
          {/* Live avatar preview */}
          <div className="hidden sm:block flex-shrink-0 pt-0.5">
            <AuthorAvatar name={name.trim() || '?' } size={38} />
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-2.5">
            <input
              type="text"
              placeholder="तपाईंको नाम (वैकल्पिक)"
              value={name}
              maxLength={30}
              onChange={(e) => setName(e.target.value)}
              className="border border-line rounded-xl px-3.5 py-2 text-[13px] outline-none bg-paper-soft w-full focus:border-crimson focus:bg-white transition-colors"
            />
            <textarea
              ref={textareaRef}
              rows={2}
              maxLength={MAX_CHARS}
              placeholder={`प्रतिक्रिया लेख्नुहोस्… (अधिकतम ${MAX_CHARS} अक्षर)`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              className="border border-line rounded-xl px-3.5 py-2.5 text-[13.5px] outline-none bg-paper-soft resize-y min-w-0 focus:border-crimson focus:bg-white transition-colors leading-relaxed"
            />
          </div>
        </div>

        {/* Footer: progress + send */}
        <div className="flex items-center justify-between gap-3 mt-2.5 sm:pl-[50px]">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="h-1.5 flex-1 max-w-[140px] rounded-full bg-paper-tint overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  nearLimit ? 'bg-crimson' : 'bg-emerald-500'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={`text-[11px] font-bold tabular-nums ${nearLimit ? 'text-crimson' : 'text-muted'}`}>
              {text.length}/{MAX_CHARS}
            </span>
          </div>
          <button
            type="button"
            aria-label="Send comment"
            disabled={!text.trim() || sending}
            onClick={submit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-crimson to-crimson-dark text-white text-[12.5px] font-bold shadow-md shadow-crimson/25 hover:shadow-lg hover:-translate-y-px active:translate-y-0 disabled:opacity-40 disabled:hover:translate-y-0 disabled:shadow-none transition-all flex-shrink-0"
          >
            <Send size={13} />
            {sending ? 'पठाउँदै…' : 'पठाउनुहोस्'}
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="flex flex-col gap-3.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-9 h-9 rounded-full bg-paper-tint flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-28 rounded-full bg-paper-tint" />
                <div className="h-3 w-full max-w-[320px] rounded-full bg-paper-tint" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && comments.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-9 rounded-2xl border border-dashed border-line bg-paper-soft/60">
          <span className="w-14 h-14 rounded-full bg-white border border-line text-muted flex items-center justify-center shadow-sm">
            <MessageCircle size={22} />
          </span>
          <p className="m-0 text-[13.5px] text-muted">अझै कुनै प्रतिक्रिया छैन — बहस सुरु गर्नुहोस्!</p>
          <button
            type="button"
            className="text-[12.5px] font-bold text-crimson hover:underline underline-offset-2"
            onClick={() => textareaRef.current?.focus()}
          >
            पहिलो प्रतिक्रिया दिनुहोस् ↑
          </button>
        </div>
      )}

      {/* Comments */}
      {!loading && comments.length > 0 && (
        <>
          <div className="flex flex-col gap-4">
            {comments.slice(0, visibleCount).map((c) => (
              <div className="flex gap-3 group" key={c.id}>
                <AuthorAvatar name={c.name} size={36} />
                <div className="flex-1 min-w-0">
                  {/* Bubble */}
                  <div className="bg-paper-soft border border-line rounded-2xl rounded-tl-md px-4 py-3 transition-shadow group-hover:shadow-md group-hover:border-emerald-200">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="font-bold text-[13px] text-ink">{c.name}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted bg-white border border-line rounded-full px-2 py-px">
                        <Clock3 size={10} />
                        {formatPublishNp(c.created_at)}
                      </span>
                    </div>
                    <p className="m-0 text-[13.5px] text-ink-soft leading-relaxed break-words">{c.comment}</p>

                    {/* Admin official reply */}
                    {c.admin_reply && (
                      <div className="mt-3 relative pl-4">
                        <span className="absolute left-0 top-0.5 bottom-0.5 w-[3px] rounded-full bg-gradient-to-b from-crimson to-crimson-dark" />
                        <div className="flex items-center gap-1.5 mb-1">
                          <ShieldCheck size={12} className="text-crimson" />
                          <span className="text-[10px] font-extrabold uppercase tracking-wider text-crimson">
                            एडमिनको जवाफ
                          </span>
                        </div>
                        <p className="m-0 text-[13px] font-bold text-crimson leading-relaxed break-words">
                          {c.admin_reply}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load more */}
          {comments.length > visibleCount && (
            <div className="flex justify-center mt-5">
              <button
                type="button"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-line bg-white text-[12.5px] font-bold text-ink-soft hover:border-crimson hover:text-crimson transition-colors shadow-sm"
              >
                <ChevronDown size={14} />
                थप प्रतिक्रिया हेर्नुहोस् ({comments.length - visibleCount})
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default CommentSection;
